import type { ComponentProps, ReactNode } from 'react';
import { MODAL_Z_INDEX } from '@constants/z-indices';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import clsx from 'clsx';

import Button from './Button';
import type { ModalIconVariant } from './ModalIcon';
import { ModalIcon } from './ModalIcon';

interface ConfirmationModalProps {
  'data-testid'?: string;
  cancelLabel?: string;
  children?: ReactNode;
  confirmDataTestId?: string;
  confirmKind?: ComponentProps<typeof Button>['kind'];
  confirmLabel: string;
  description?: ReactNode;
  hideCancel?: boolean;
  icon?: ModalIconVariant;
  isLoading?: boolean;
  isOpen?: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
}

export const ConfirmationModal = ({
  'data-testid': dataTestId,
  cancelLabel = 'Cancel',
  children,
  confirmDataTestId,
  confirmKind = 'primary',
  confirmLabel,
  description,
  hideCancel,
  icon = 'warning',
  isLoading,
  isOpen = true,
  onClose,
  onConfirm,
  title,
}: ConfirmationModalProps) => (
  <Dialog
    as="div"
    open={isOpen}
    className={clsx('fixed inset-0 overflow-y-auto', MODAL_Z_INDEX)}
    onClose={onClose}
    data-testid={dataTestId}
  >
    <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div className="bg-info-500/75 fixed inset-0 transition-opacity" />

      <DialogPanel
        transition
        className="flex h-screen items-center justify-center duration-300 ease-out data-closed:transform-[scale(95%)] data-closed:opacity-0"
      >
        <div className="relative inline-block transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-[600px] sm:p-6 sm:align-middle">
          <div className="absolute top-0 right-0 hidden pt-4 pr-4 sm:block">
            <button
              type="button"
              className="text-info-400 hover:text-info-500 rounded-md bg-white focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:outline-hidden"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <XIcon className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="sm:flex sm:items-start">
            <ModalIcon variant={icon} />
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <DialogTitle as="h3" className="text-info-900 text-lg leading-6 font-medium">
                {title}
              </DialogTitle>
              {description !== undefined && (
                <div className="mt-2">
                  <p className="text-info-500 text-sm">{description}</p>
                </div>
              )}
              {children}
            </div>
          </div>
          <div className="mt-8 items-center justify-end gap-x-2.5 sm:mt-8 sm:flex">
            {!hideCancel && (
              <Button
                className="focus h-auto px-4 py-2"
                onClick={onClose}
                kind="secondary"
                size="sm"
                isDisabled={isLoading}
              >
                {cancelLabel}
              </Button>
            )}
            <Button
              loading={isLoading}
              className="h-auto px-4 py-2"
              onClick={onConfirm}
              kind={confirmKind}
              size="sm"
              data-testid={confirmDataTestId}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
);
